import type { InstanceLaunchedEvent } from '../../../services/playjector/interfaces/IPlayjectorService';

/**
 * Sink for `processInstanceLaunchedMSG` notifications. Structural subset of
 * `GameInstanceLaunchManager` — keeps this handler testable without the
 * coordinator graph.
 */
export type InstanceLaunchedHandler = {
  /** Returns true when the event matched a pending per-instance launch wait. */
  handleInstanceLaunched(event: InstanceLaunchedEvent): boolean;
};

/**
 * Android multi-instance (host Phase 3): route per-instance launch replies
 * into the launch manager's pending waits.
 *
 * One file, one message. The production wiring lives in
 * `GameLaunchCoordinator`, which subscribes the handler built here to
 * `PlayjectorService.onInstanceLaunched` (the service taps the
 * `process/instanceLaunched` mainMessage channel and gates forwarding on the
 * registered streamId). `instanceClosed` is Phase 4 and deliberately has no
 * handler yet.
 */
export const createInstanceLaunchedHandler = (
  getManager: () => InstanceLaunchedHandler,
) => {
  return (event: InstanceLaunchedEvent): void => {
    const handled = getManager().handleInstanceLaunched(event);
    if (!handled) {
      // Late or stale reply (timed out earlier, or from a torn-down launch) —
      // logged and dropped, matching the launch manager's no-op contract.
      console.warn(
        `[InstanceLaunched-Handler] No pending launch wait for stream "${event.streamId}" ` +
        `instance ${event.gameInstanceIndex} — dropping`,
      );
    }
  };
};

/**
 * Sink for relaunch echoes the launch manager did not claim. Structural
 * subset of `InstanceRecoveryManager` (android-multi-instance-host Phase 4).
 */
export type InstanceRecoveryEchoSink = {
  /** Returns true when the echo matched a restarting instance. */
  onInstanceLaunched(event: InstanceLaunchedEvent): boolean;
};

/**
 * Android multi-instance (host Phase 8, review fix B1): composite echo
 * routing. The launch manager gets FIRST claim on every `instanceLaunched`
 * echo (it owns launch-path replies); an echo it does not claim is a restart
 * relaunch echo and goes to the recovery policy. Without this hop the
 * recovery manager's `onInstanceLaunched` is dead in production — the
 * GameLaunchCoordinator constructor subscription routes to the launch
 * manager only — and policy restarts wait out the full echo timeout on every
 * attempt, looping until the restart budget exhausts.
 *
 * Production wiring lives in `coordinators/init.ts`, which subscribes the
 * handler built here to `PlayjectorService.onInstanceLaunched` alongside the
 * launch manager's own subscription.
 */
export const createCompositeInstanceLaunchedHandler = (
  getManager: () => InstanceLaunchedHandler,
  getRecoverySink: () => InstanceRecoveryEchoSink,
) => {
  return (event: InstanceLaunchedEvent): void => {
    if (getManager().handleInstanceLaunched(event)) return;
    if (getRecoverySink().onInstanceLaunched(event)) return;
    // Neither the launch path nor the recovery policy claimed the echo —
    // stale/late (timed out earlier, torn-down launch, or a stream from a
    // previous campaign). Logged and dropped, never thrown.
    console.warn(
      `[InstanceLaunched-Handler] Echo for stream "${event.streamId}" ` +
      `instance ${event.gameInstanceIndex} claimed by neither launch nor recovery — dropping`,
    );
  };
};
